import type { ItemFieldEntry } from '@cheqroom/api';
import { FC, PropsWithChildren } from 'react';

import { Barcode, QRCode } from '../Info/components';
import {
	Field,
	type FieldOverrideProps,
	type FieldRenderOptions,
	Fields,
	type InfoField,
	InfoFields,
	orderFieldsByDefs,
	resolveCustomFields,
} from '../Info/fields';
import type { StackProps } from '../Stack';
import { FieldRenderer, Status } from './components';
import { KitInfoKit, KitInfoKitProvider } from './KitInfo.context';
import type { KitInfoFieldId } from './KitInfo.types';
import { resolveSystemFieldsFromKit } from './utils';

interface ComponentComposition {
	Status: typeof Status;
	Field: FC<FieldOverrideProps<KitInfoFieldId>>;
	Fields: typeof Fields;
	QRCode: typeof QRCode;
	Barcode: typeof Barcode;
}

export interface Props extends StackProps {
	kit?: KitInfoKit;
	fieldDefs?: ItemFieldEntry[];
	extraFields?: InfoField[];
	isLoading?: boolean;
}

const renderKitField = (field: InfoField, options: FieldRenderOptions) => <FieldRenderer {...options} field={field} />;

const KitInfo: FC<PropsWithChildren<Props>> & ComponentComposition = ({
	kit = {} as KitInfoKit,
	fieldDefs,
	children,
	...rest
}) => {
	const fields = orderFieldsByDefs(
		[...resolveSystemFieldsFromKit(kit, fieldDefs), ...resolveCustomFields(kit.fields ?? {}, fieldDefs)],
		fieldDefs
	);

	return (
		<KitInfoKitProvider kit={kit} fieldDefs={fieldDefs}>
			<InfoFields {...rest} fieldDefs={fieldDefs} fields={fields} renderField={renderKitField}>
				{children}
			</InfoFields>
		</KitInfoKitProvider>
	);
};

KitInfo.Status = Status;
KitInfo.Field = Field;
KitInfo.Fields = Fields;
KitInfo.QRCode = QRCode;
KitInfo.Barcode = Barcode;

export { KitInfo };
